import { api } from '@/api/client';
import type { TierBand } from '@/api/onderaannemers';

// ── Onderaannemer PO editing (Fase 2) ─────────────────────────────────────────
// Edit ONE open rolling PO (draft `[LOPEND]`): reorder lines/section blocks, add
// section/collector/minprijs/km lines, edit m²/price/tier/label, delete lines.
// admin-only; the backend guards editable POs and holds a per-PO lock. Contract
// is frozen (backend is live-verified on TEST). {ok,data} envelope via `api()`.

export type PoLineType =
  | 'section'
  | 'collector'
  | 'minprijs'
  | 'km'
  | 'werkuren'
  | 'meerwerk'
  | 'manual';

export interface PoLineAnalytic {
  id: number;
  name: string;
}

export interface PoEditLine {
  id: number;
  sequence: number;
  displayType: 'line_section' | 'line_note' | false;
  rawName: string;
  label: string;
  lineType: PoLineType;
  productId: number | null;
  collectorId: number | null;
  meerwerkId: number | null;
  // Deep-link target: the collector's project (section headers inherit their first
  // child collector's), so the project label links to its dashboard on that fase.
  projectId: number | null;
  m2: number | null;
  rate: number | null;
  subtotal: number | null;
  date: string | null; // YYYY-MM-DD
  // Hidden `[portal:*]` sentinel notes → rendered greyed, non-draggable, non-editable,
  // but KEPT in the list so the reorder id-set stays complete.
  locked: boolean;
  analytic: PoLineAnalytic | null;
}

export interface PoEditDetail {
  poId: number;
  poName: string;
  vendorId: number;
  vendorName: string;
  origin: string;
  amountUntaxed: number;
  editable: true;
  lines: PoEditLine[];
}

// ── Add-line payloads (discriminated by `kind`) ───────────────────────────────

export interface AddSectionLine {
  kind: 'section';
  projectId: number;
  date?: string | null;
}

export interface AddCollectorLine {
  kind: 'collector';
  projectId: number;
  label?: string;
  systeem: string;
  legpatroon: number;
  m2: number;
  tier?: TierBand;
  priceOverride?: number;
  collectorId?: number;
}

export interface AddMinprijsLine {
  kind: 'minprijs';
  projectId: number;
  count: number;
}

export interface AddKmLine {
  kind: 'km';
  projectId: number;
  km: number;
}

export interface AddMeerwerkLine {
  kind: 'meerwerk';
  projectId: number;
  description: string;
  amount: number;
  qty?: number;
}

export type AddPoLinePayload =
  | AddSectionLine | AddCollectorLine | AddMinprijsLine | AddKmLine | AddMeerwerkLine;

export interface PatchPoLinePayload {
  m2?: number;
  rate?: number;
  tier?: TierBand;
  label?: string;
}

// ── Endpoints ─────────────────────────────────────────────────────────────────

/** Fetch the editable detail for ONE open PO. */
export async function fetchPoDetail(poId: number): Promise<PoEditDetail> {
  return api<PoEditDetail>(`/api/onderaannemers/po/${poId}`);
}

/**
 * Reorder the PO's lines. `orderedLineIds` is the COMPLETE set of the PO's line ids
 * (including hidden locked rows) in the new order. On a `409 REORDER_MISMATCH` the
 * caller refetches — the tab drifted.
 */
export async function reorderPoLines(
  poId: number,
  orderedLineIds: number[],
): Promise<{ lines: Array<{ id: number; sequence: number }> }> {
  return api<{ lines: Array<{ id: number; sequence: number }> }>(
    `/api/onderaannemers/po/${poId}/reorder`,
    { method: 'POST', body: JSON.stringify({ orderedLineIds }) },
  );
}

/** Add one line at the end of the PO (drag into place afterwards). */
export async function addPoLine(
  poId: number,
  payload: AddPoLinePayload,
): Promise<{ line: PoEditLine }> {
  return api<{ line: PoEditLine }>(`/api/onderaannemers/po/${poId}/lines`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Edit an existing line (m² / rate / tier / section label). At least one field. */
export async function patchPoLine(
  poId: number,
  lineId: number,
  patch: PatchPoLinePayload,
): Promise<{ line: PoEditLine }> {
  return api<{ line: PoEditLine }>(`/api/onderaannemers/po/${poId}/lines/${lineId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * Delete a line. `cascade` (section block delete) is a QUERY PARAM only — the backend
 * 500s on an empty JSON body, so this sends NO body and NO Content-Type header (the
 * shared `api()` client only sets Content-Type when a body is present).
 */
export async function deletePoLine(
  poId: number,
  lineId: number,
  cascade = false,
): Promise<{ deletedIds: number[] }> {
  const qs = cascade ? '?cascade=1' : '';
  return api<{ deletedIds: number[] }>(
    `/api/onderaannemers/po/${poId}/lines/${lineId}${qs}`,
    { method: 'DELETE' },
  );
}
